export function getEndOfDate(date: Date): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23, // Hours
        59, // Minutes
        59, // Seconds
        999 // Milliseconds
    );
}

export function getBeginningOfDate(date: Date): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, // Hours
        0, // Minutes
        0, // Seconds
        0 // Milliseconds
    );
}

export function addDays(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
}

export function addTime(
    date: Date,
    { hours = 0, minutes = 0, seconds = 0 }: { hours?: number; minutes?: number; seconds?: number } = {}
): Date {
    const newDate = new Date(date);

    if (hours) newDate.setHours(newDate.getHours() + hours);
    if (minutes) newDate.setMinutes(newDate.getMinutes() + minutes);
    if (seconds) newDate.setSeconds(newDate.getSeconds() + seconds);

    return newDate;
}

export function setEndOfWeek(date: Date): Date {
    // Starts at 0 (Sunday)
    const currentDay = date.getDay();
    const daysToAdd = 7 - currentDay;

    return addDays(date, daysToAdd);
}

export function getTimeFormat(date: Date, timeZone?: string): string {
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    return date.toLocaleTimeString('en-US', options);
}

export function getDateFormatString(date: Date, timeZone?: string): string {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    return `${year}-${month}-${day}`;
}

// =======  Functions based on YYYY-MM-DD date string  ======= //
export function getDayMonthYearFromString(dateString: string): { year: number; month: number; day: number } {
    if (!dateString) return { year: 0, month: 0, day: 0 };
    // dateString format = YYYY-MM-DD
    const [year, month, day] = dateString.split('-').map((v) => Number(v));
    // Date month list goes from 0 to 11.
    return { year, month: month - 1, day };
}

function getExactDate(dateString: string) {
    // dateString format = YYYY-MM-DD
    const { year, month, day } = getDayMonthYearFromString(dateString);
    return new Date(year, month, day);
}

export function getMonthYearFromString(dateString: string) {
    // dateString format = YYYY-MM-DD
    const date = getExactDate(dateString);
    // returns e.g. "Jan 2025"
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function getFullMonthYearFromString(dateString: string) {
    // dateString format = YYYY-MM-DD
    const date = getExactDate(dateString);
    // returns e.g. "January 2025"
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function getMonthAndDateFromString(dateString: string) {
    // dateString format = YYYY-MM-DD
    const date = getExactDate(dateString);
    // returns e.g. "Mon Jan 12"
    return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'numeric' });
}

export function getMonthNameFromString(dateString: string): string {
    // dateString format = YYYY-MM-DD
    const date = getExactDate(dateString);
    // returns e.g. "January"
    return date.toLocaleDateString('en-US', { month: 'long' });
}

export function getMonthDateFromString(dateString: string) {
    // dateString format = YYYY-MM-DD
    const date = getExactDate(dateString);
    // returns e.g. "Jan 12"
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDateMonthYear(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', day: 'numeric' });
}
